import { defineStore } from 'pinia';
import DocNetDashboardService from 'src/services/docnet/DocNetDashboardService';
import { preferencesService } from 'src/services/preferences/preferencesService';
import { docnet_dashboard_layout } from 'src/constants';
import {
  DEFAULT_LAYOUT,
  parseLayout,
  getWidgetMeta,
  getRequiredWidgetKeys,
} from 'src/pages/DocNet/Dashboard/widgets/registry';
import type {
  DocNetDashboardStats,
  DocNetDashboardStatsResponse,
  DocNetDashboardTrendsResponse,
  DocNetDashboardWidgetsResponse,
  DocNetDashboardLayout,
  DocNetWidgetAgentFleet,
  DocNetWidgetAgentAtWork,
  DocNetWidgetActiveMission,
  DocNetWidgetOpenIssue,
  DocNetWidgetBlockedItem,
  DocNetWidgetNeedsApproval,
  DocNetWidgetRecentActivity,
} from 'src/@types/docnet';

const dashboardService = new DocNetDashboardService();

export const useDocNetDashboardStore = defineStore('docnet_dashboard', {
  state: () => ({
    loading: false,

    // Backend stats (/dashboard/stats)
    backendStats: null as DocNetDashboardStatsResponse | null,
    statsLoading: false,
    statsError: false,

    // Trends (/dashboard/trends)
    trends: null as DocNetDashboardTrendsResponse | null,
    trendsLoading: false,
    trendsError: false,

    // Widget data (/dashboard/widgets)
    widgetData: null as DocNetDashboardWidgetsResponse | null,
    widgetsLoading: false,
    widgetsError: false,

    // Layout config (org preferences)
    layout: DEFAULT_LAYOUT as DocNetDashboardLayout,
    layoutLoading: false,
  }),

  getters: {
    // Legacy KPI getter
    stats(): DocNetDashboardStats {
      if (this.backendStats) {
        const s = this.backendStats;
        return {
          open_tasks: s.issues.open,
          pending_approvals: s.inbox.pending,
          active_missions: s.missions.active,
          agents_working: s.agents.enabled,
          blocked_items: s.issues.blocked,
        };
      }
      return {
        open_tasks: 0,
        pending_approvals: 0,
        active_missions: 0,
        agents_working: 0,
        blocked_items: 0,
      };
    },

    // Widget data accessors
    agentFleet(): DocNetWidgetAgentFleet[] {
      return this.widgetData?.agent_fleet || [];
    },

    agentsAtWork(): DocNetWidgetAgentAtWork[] {
      return this.widgetData?.agents_at_work || [];
    },

    activeMissions(): DocNetWidgetActiveMission[] {
      return this.widgetData?.active_missions || [];
    },

    missionsByStatus(): Record<string, number> {
      return this.widgetData?.missions_by_status || {};
    },

    openIssues(): DocNetWidgetOpenIssue[] {
      return this.widgetData?.open_issues || [];
    },

    blockedItems(): DocNetWidgetBlockedItem[] {
      return this.widgetData?.blocked_items || [];
    },

    pendingApprovals(): DocNetWidgetNeedsApproval[] {
      return this.widgetData?.needs_approval || [];
    },

    recentActivity(): DocNetWidgetRecentActivity[] {
      return this.widgetData?.recent_activity || [];
    },
  },

  actions: {
    async loadAll(): Promise<void> {
      this.loading = true;
      try {
        // Load layout first so we know which widgets are enabled
        await this.loadLayout();

        // Fire stats + trends + widgets in parallel
        const widgetKeys = getRequiredWidgetKeys(this.layout.enabled_widgets);

        const results = await Promise.allSettled([
          this.loadStats(),
          this.loadTrends(),
          this.loadWidgets(widgetKeys),
        ]);

        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length > 0) {
          console.warn(
            `[DocNet] ${failures.length}/${results.length} dashboard calls failed:`,
            failures.map((f) => (f as PromiseRejectedResult).reason),
          );
        }

        if (failures.length === results.length) {
          throw new Error('All dashboard data calls failed');
        }
      } catch (error) {
        console.error('[DocNet] Failed to load dashboard data:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loadStats(): Promise<void> {
      this.statsLoading = true;
      this.statsError = false;
      try {
        this.backendStats = await dashboardService.getStats();
      } catch {
        this.statsError = true;
      } finally {
        this.statsLoading = false;
      }
    },

    async loadTrends(): Promise<void> {
      this.trendsLoading = true;
      this.trendsError = false;
      try {
        this.trends = await dashboardService.getTrends(14);
      } catch {
        this.trendsError = true;
      } finally {
        this.trendsLoading = false;
      }
    },

    async loadWidgets(keys: string[]): Promise<void> {
      if (keys.length === 0) {
        this.widgetData = {};
        return;
      }
      this.widgetsLoading = true;
      this.widgetsError = false;
      try {
        this.widgetData = await dashboardService.getWidgets(keys);
      } catch {
        this.widgetsError = true;
      } finally {
        this.widgetsLoading = false;
      }
    },

    async loadLayout(): Promise<void> {
      this.layoutLoading = true;
      try {
        const raw = await preferencesService.getOrgPreference(
          docnet_dashboard_layout,
        );
        this.layout = parseLayout(raw);
      } catch {
        this.layout = DEFAULT_LAYOUT;
      } finally {
        this.layoutLoading = false;
      }
    },

    async saveLayout(layout: DocNetDashboardLayout): Promise<void> {
      await preferencesService.setOrgPreference(
        docnet_dashboard_layout,
        JSON.stringify(layout),
      );
      this.layout = layout;
    },
  },
});
